import type { IndexMembershipResult } from "@mosaic/shared";

/**
 * Which major indexes held this company at the last quarter end.
 *
 * **Read from a fund's holdings, not from an index-membership feed**, since there is no
 * free one. Every registered fund files its portfolio quarterly on NPORT-P, so "is this in
 * the S&P 500" becomes "did the fund tracking it hold it at the last quarter end". Two
 * properties come with that answer, and both are on screen.
 *
 * The data is a quarter old. And **`not_matched` is not "not in the index"**: a holding
 * names no CIK and no ticker, so the match is on a canonical name, and EDGAR stores some
 * names surname-first (`HORTON D R INC`) where a fund writes `DR Horton Inc`. An unmatched
 * member is indistinguishable from an absent company, so the panel says only what it
 * knows.
 *
 * What index rules do at a distribution is a different question this does not answer;
 * see `server/bench/fixtures/INDEX-INCLUSION.md`.
 */
export function IndexMembershipValue({ result }: { result: IndexMembershipResult | undefined }) {
  if (!result) return null;

  if (result.status === "no_holdings") {
    return <span className="ix-none">index holdings not fetched yet</span>;
  }
  if (result.status === "unnamed") {
    return <span className="ix-none">not yet named in the filings</span>;
  }
  if (result.status === "not_matched") {
    return (
      <span className="ix-none">
        not among the {result.checked.length === 1 ? "index" : "indexes"} read
        <span
          className="ix-caveat"
          title={
            `Matched by company name against fund holdings as of ${result.asOf}. ` +
            `That resolves ${result.resolutionPct}% of holdings — EDGAR writes some ` +
            `companies surname-first where a fund writes them naturally, so this is not ` +
            `proof of absence.`
          }
        >
          {" "}
          ({result.resolutionPct}% name match)
        </span>
      </span>
    );
  }

  return (
    <span className="ix">
      {result.memberships.map((m) => (
        <span key={m.index} className="ix-one">
          <span className="ix-label">{m.label}</span>
          {/* The lag is the caveat, so it is beside the claim rather than in a footnote. */}
          <span className="ix-asof">
            as of {m.asOf} · {m.ageDays}d ago
          </span>
        </span>
      ))}
    </span>
  );
}
